// ── WHICH ELECTION THE CAMPAIGN IS IN (0413, 0415) ───────────────────────────────────────────────
// What GET /campaign/election answers: the campaign's election, where it is on its path, what it
// could change to, and -- for managers, the day after an election on the path -- the question.
import { api } from "./api";

export type Election = {
  id: string; day: string; kind: string; label: string; early_start: string | null; early_end: string | null;
  status: "approved" | "pending"; source: "law" | "staff" | "campaign"; passed: boolean; current: boolean;
};
export type CampaignElection = {
  today: string; path: "partisan" | "local"; chosen: boolean; can_manage: boolean; district: string | null;
  current: Election | null; steps: Election[]; options: Election[];
  // Already-voted lists filed under the current election's day: the campaign's own, and Orator's.
  lists_on_day: { own: number; orator: number };
  banner: { prev: Election; ask_runoff: boolean; yes: Election | null; no: Election | null; next: Election | null } | null;
};

export const loadElection = (campaignId: string) =>
  api<CampaignElection>(`/campaign/election?campaign_id=${campaignId}`);

// Settings tells the banner it was answered, so the banner goes without a reload.
export const ELECTION_CHANGED = "orator:election-changed";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
/** An election day as the calendar writes it -- never through Date(): "2026-11-03" read as an instant
 *  is midnight UTC, which is the 2nd in Texas. */
export function electionDay(iso: string | null, opts: { year?: boolean; short?: boolean } = {}): string {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null;
  if (!m) return "—";
  const mon = MONTHS[Number(m[2]) - 1];
  const d = `${opts.short ? mon.slice(0, 3) : mon} ${Number(m[3])}`;
  return opts.year === false ? d : `${d}, ${m[1]}`;
}
/** "Oct 19 – Oct 30" */
export function earlyVoting(e: Election | null): string {
  if (!e?.early_start) return "—";
  return `${electionDay(e.early_start, { year: false, short: true })} – ${electionDay(e.early_end, { year: false, short: true })}`;
}
/** "General election · Nov 3, 2026" */
export const electionName = (e: Election) => `${e.label} · ${electionDay(e.day, { short: true })}`;
/** "the March 3 primary", for a sentence. */
export const electionInSentence = (e: Election) => `the ${electionDay(e.day, { year: false })} ${e.label.toLowerCase()}`;
/** The short name a path step uses: "Primary", "Runoff", "General". */
export function stepName(e: Election): string {
  return ({ primary: "Primary", primary_runoff: "Runoff", general: "General", may: "May election",
            local_runoff: "Runoff" } as Record<string, string>)[e.kind] ?? e.label;
}
